import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import copy from 'copy-to-clipboard'
import { Field, Form } from 'factorial-form'

import { observer } from 'mobx-react'

import BrowserBarPreview from 'modules/core/components/BrowserBarPreview'
import { SITES, SIZES } from 'modules/core/components/BrowserBarPreview/BrowserImage'
import FormRow from 'modules/core/components/FormRow'
import HelpCenterLink from 'modules/core/components/HelpCenterLink'
import InputText from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import Steps from 'modules/core/components/Steps'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import atsCompanies from 'modules/ats/store/collections/atsCompanies'

type TouchableProps = React.ComponentProps<typeof Button>

interface Props {
  onClose: () => void
}

type Step = 1 | 2

const LinkedInModal = ({ onClose }: Props) => {
  const [step, setStep] = React.useState<Step>(1)
  const atsCompany = atsCompanies.current
  const linkedInId = atsCompany ? atsCompany.get('linkedin_id') || '' : ''

  const {
    access: { firstName },
    company: { name: companyName, publicUrl },
  } = useCurrentSession()

  const form = React.useRef(
    new Form(
      {
        linkedin_id: linkedInId,
      },
      {
        linkedin_id: 'string',
      }
    )
  ).current

  const copyText: (text: string) => TouchableProps['onClick'] = (text) => (event) => {
    event.preventDefault()
    text && copy(text)
    app.setFlash({
      type: 'correct',
      message: i18n.t('common.copied'),
    })
  }

  const { running: saving, performAction: onSave } = useAsyncAction(async () => {
    if (atsCompany) {
      await form.save(atsCompany)
    }

    setStep(2)
  })

  const onBack = () => {
    setStep(1)
  }

  const requestDescription = i18n.t(
    'app_store_v2.integrations.linkedin.modal.request_description',
    {
      name: firstName,
      company: companyName,
      careers_link: `${publicUrl}/xml_feed.xml`,
      linkedin_id: linkedInId,
    }
  )

  const getStepTitle = () => {
    switch (step) {
      case 1:
        return i18n.t(`app_store_v2.integrations.linkedin.modal.title_1`)
      case 2:
        return i18n.t(`app_store_v2.integrations.linkedin.modal.title_2`)
    }
  }

  const getStepDescription = () => {
    switch (step) {
      case 1:
        return i18n.t(`app_store_v2.integrations.linkedin.modal.description_1`)
      case 2:
        return i18n.t(`app_store_v2.integrations.linkedin.modal.description_2`)
    }
  }

  return (
    <Modal
      illustration="sync"
      title={getStepTitle()}
      description={getStepDescription()}
      onClose={onClose}
      footer={
        <>
          {step !== 1 && (
            <Button onClick={onBack} type="secondary">
              {i18n.t('common.back')}
            </Button>
          )}

          {step === 1 && (
            <Button disabled={saving} onClick={onSave}>
              {i18n.t('common.save_and_continue')}
            </Button>
          )}

          {step === 2 && <Button onClick={onClose}>{i18n.t('common.save')}</Button>}
        </>
      }
    >
      <Box paddingBottom="s24">
        <Steps step={step} steps={2} />
      </Box>

      {step === 1 && (
        <>
          <FormRow>
            <InputText
              label={i18n.t('app_store_v2.integrations.linkedin.modal.title_1')}
              field={form.get('linkedin_id')}
            />
          </FormRow>
          <FormRow>
            <BrowserBarPreview
              size={SIZES.big}
              site={SITES.linkedin}
              domain={i18n.t('app_store_v2.integrations.linkedin.modal.domain', {
                id: i18n.t('app_store_v2.integrations.linkedin.modal.id'),
              })}
            />
          </FormRow>
          <Box paddingTop="s16">
            <HelpCenterLink name="share_job_offer_on_linkedin">
              {i18n.t('app_store_v2.integrations.linkedin.modal.help_center')}
            </HelpCenterLink>
          </Box>
        </>
      )}
      {step === 2 && (
        <Box gap="s24">
          <TextArea
            readonly
            label={i18n.t('app_store_v2.integrations.linkedin.modal.request')}
            field={new Field(requestDescription, 'string')}
          />
          <Button onClick={copyText(requestDescription)} type="secondary">
            {i18n.t('app_store_v2.integrations.linkedin.modal.request_copy')}
          </Button>
        </Box>
      )}
    </Modal>
  )
}

export default observer(LinkedInModal)
